import React, { useState, useEffect } from 'react';
import { Grid, IconButton, Tooltip } from '@mui/material';
import { GridActionsCellItem } from '@mui/x-data-grid-pro';
import CreateIcon from '@mui/icons-material/Create';
import MapOutlined from '@mui/icons-material/MapOutlined';
import {
    DataGrid,
    BreadCrumbs,
    PageTitleWithToolTip,
    AddButton
} from '../Common';
import { BadgeWithLinkedText } from '../Common/DataGrid/CellComponents';
import { getDateRangeFilterOperator } from '../Common/DataGrid/FilterComponents';
import { GetLocationFilters } from "../../CodesCache";
import {
    getFilterOptions,
    dataGridContainerProps,
    text_commonCellProps,
    select_commonCellProps,
} from '../Common/DataGrid/utils';
import Routes from '../../routes';
import UserInfo from '../../UserInfo';
import { useBreadCrumbs, useNavigate } from '../../hooks';
import { AddDatabase } from '../../Icons/Icons';
import { trakrefBlue, Grey2 } from '../../color_utils';

const dateRangeFilterOperator = getDateRangeFilterOperator({});

const Locations = ({ history = {} }) => {
    const [filters, setFilters] = useState([]);
    const [canEdit, setCanEdit] = useState(false);
    const navigate = useNavigate({ pageName:'Locations' });

    useBreadCrumbs({
        path: { id: 'Locations', text: 'Locations', link: history.location.pathname, reset: true }
    });

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        const data = await GetLocationFilters();
        setFilters(data);

        setCanEdit(UserInfo.isNativeAdminRole());
    };

    const columns = [
        {
            field: 'name',
            headerName: 'Location Name',
           ...text_commonCellProps,
            renderCell: ({ row: { status, id, name } }) => (
                <BadgeWithLinkedText
                    statusProperty={status}
                    text={name}
                    to={{
                        pathname: `/Location/${id}`,
                        state: {
                            from: window.location.pathname
                        }
                    }}
                />
            )
        },
        {
            field: 'code',
            headerName: 'Code',
            ...text_commonCellProps
        },
        {
            field: 'locationType',
            headerName: 'Location Type',
            ...select_commonCellProps
        },
        {
            field: 'status',
            headerName: 'Status',
            initiallyVisible: false,
            ...select_commonCellProps
        },
        {
            field: 'operationDate',
            headerName: 'Start Date',
            valueGetter: (date) => date,
            filterOperators: dateRangeFilterOperator,
            flex: 1,
            initiallyVisible: false,
        },
        {
            field: 'operationEndDate',
            headerName: 'End Date',
            valueGetter: (date) => date,
            filterOperators: dateRangeFilterOperator,
            flex: 1,
            initiallyVisible: false
        },
        {
            field: 'activeAssetCount',
            headerName: 'Active System Count',
            filterable: false,
            sortable: false,
            flex: 1,
        },
        {
            field: 'regionDivision',
            headerName: 'Region/Division',
            ...select_commonCellProps
        },
        {
            field: 'classification',
            headerName: 'Classification',
            initiallyVisible: false,
            ...select_commonCellProps
        },
        {
            field: 'physicalAddress1',
            headerName: 'Address',
            ...text_commonCellProps,
            renderCell: ({ row: { locationMap, physicalAddress1 } }) => {
                if (physicalAddress1) {
                    return (
                        <Tooltip title={"View on Google Maps"}>
                            <IconButton
                                sx={{ color: trakrefBlue }}
                                onClick={() => window.open(locationMap, "_blank")}
                            >
                                <MapOutlined />
                            </IconButton>
                            {physicalAddress1}
                        </Tooltip>
                    );
                }
                return null;
            }
        },
        {
            field: 'physicalCity',
            headerName: 'City',
            initiallyVisible: false,
            ...select_commonCellProps
        },
        {
            field: 'physicalState',
            headerName: 'State',
            ...select_commonCellProps
        },
        {
            field: 'physicalZip',
            headerName: 'Zip',
            initiallyVisible: false,
            ...text_commonCellProps
        },
        {
            field: 'physicalCountry',
            headerName: 'Country',
            initiallyVisible: false,
            ...select_commonCellProps
        },
        {
            field: 'contactName',
            headerName: 'Contact',
            initiallyVisible: false,
            ...select_commonCellProps
        },
        {
            field: 'phone',
            headerName: 'Phone #',
            initiallyVisible: false,
            ...text_commonCellProps
        },
        {
            field: 'email',
            headerName: 'Email',
            initiallyVisible: false,
            ...text_commonCellProps
        },
        {
            field: 'contact2',
            headerName: 'Contact 2',
            initiallyVisible: false,
            ...select_commonCellProps
        },
        {
            field: 'phone2',
            headerName: 'Phone # 2',
            filterable: false,
            sortable: false,
            flex: 1,
            initiallyVisible: false
        },
        {
            field: 'email2',
            headerName: 'Email2',
            filterable: false,
            sortable: false,
            flex: 1,
            initiallyVisible: false
        },
        {
            field: 'alias',
            headerName: 'Alias',
            initiallyVisible: false,
            ...text_commonCellProps
        },
        {
            type: 'actions',
            field: 'Quick Actions',
            width: 100,
            getActions: ({ row: { id } }) => {
                const quickActions = [];
                const isSystemOwners = UserInfo.isSuperTenantAdmin() || UserInfo.isTenantAdministrator() || UserInfo.isRegionalAdministrator();
                const isSystemOwner =
                    UserInfo.isSystemOwner() && (
                    UserInfo.isSuperTenantAdmin() || 
                    UserInfo.isTenantAdministrator() || 
                    UserInfo.isRegionalAdministrator() ||
                    UserInfo.isTechnicianManager() ||
                    UserInfo.isTechnician()
                );
                const isContractorLite = 
                    UserInfo.isServiceProvider() && (
                    UserInfo.isTenantAdministrator() ||
                    UserInfo.isTechnicianManager() ||
                    UserInfo.isTechnician()
                );
                const renderEditLocation = (
                    UserInfo.isSystemAdministrator()
                    || (
                        UserInfo.isSystemOwner()
                        && isSystemOwners
                        && !UserInfo.isNativeAccountServiceProvider()
                    )
                );
                const renderCreateInventory = (
                    UserInfo.isSystemAdministrator() || isSystemOwner || isContractorLite
                );
                if (renderEditLocation) {
                    quickActions.push(
                        <GridActionsCellItem
                            icon={
                                <Tooltip
                                    title="Edit Location"
                                >
                                    <CreateIcon sx={{ color: Grey2 }} />
                                </Tooltip>
                            }
                            onClick={() => navigate.push(`/EditLocation/${id}`)}
                        />
                    );
                }
                if (renderCreateInventory) {
                    quickActions.push(
                        <GridActionsCellItem
                            // Since we are using a custom icon here (not MUI-icon), we must remove padding
                            // to center the icon within the hover background color
                            sx={{ paddingBottom: "0%" }}
                            icon={
                                <Tooltip
                                    title="Create Inventory"
                                >
                                    <span>
                                        <AddDatabase sx={{ color: Grey2 }} />
                                    </span>
                                </Tooltip>
                            }
                            onClick={() => navigate.push(
                                `/AddInventory`,
                                null,
                                { parentAssetLocationID: id })
                            }
                        />
                    );
                }
                return quickActions;
            }
        },
    ];

    return (
        <>
            <BreadCrumbs
                path={[
                    { text: 'Locations' }
                ]}
            />
            <Grid
                container
                direction="row"
                alignItems="center"
            >
                <Grid item xs={6} md={6} lg={6}>
                    <PageTitleWithToolTip
                        pageTitle="Locations"
                        toolTipText="Locations are physical facilities were refrigerant assets are housed"
                    />
                </Grid>
                {(canEdit) && (
                    <Grid item xs={6} md={6} lg={6} align="right">
                        <AddButton
                            text="Create Location"
                            onClick={() => navigate.push("/CreateLocation")}
                        />
                    </Grid>
                )}
                <Grid item { ...dataGridContainerProps }>
                    <DataGrid
                        url={Routes.Location.LocationTable()}
                        exportButtonURL={Routes.Location.LocationTableExport()}
                        exportButtonAlt="Export Locations (.csv)"
                        filterOptions={filters}
                        noResultText="Oops...It is empty here..."
                        noResultText2="Create a new Location or change your search criterias."
                        tableId={"location-table"}
                        columns={columns}
                    />
                </Grid>
            </Grid>
        </>
    );
}

export default Locations;
